import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Composition : famille "recette / nomenclature".
 *
 * Décrit comment un article est composé : ses composants (avec quantités), les
 * étapes de préparation ou d'assemblage, le rendement produit et un coût
 * calculé. Couvre aussi bien une recette de cuisine qu'une nomenclature
 * industrielle (bill of materials). Construite sur le patron "famille + type"
 * (cf. asset).
 *
 * Le socle ne connaît pas la nature concrète : il connaît la famille générique
 * `composition`, et le vocabulaire métier est porté par le champ `type` (chaîne
 * libre déclarée par l'app : "recipe", "bom"...).
 *
 * Article composé (`productId`) : cœur de la famille. Une composition décrit
 * toujours la fabrication d'UN article, donc `productId` est requis (comme
 * contactId l'est pour order). Les composants pointent d'autres produits via
 * leur `productId` à l'intérieur de la liste `components` (structure libre).
 *
 * Sens des références : `composition` porte son article cible (`productId`).
 * Les liens stock, stock-movement et order sont AVAL / opérationnels : c'est
 * l'autre entité qui porte `compositionId` de son côté (un mouvement de stock
 * pointe la composition consommée), on ne les stocke donc pas ici. Aucun import
 * d'un autre schéma de famille : les références restent de simples ids.
 *
 * Accès : v0 reste org-wide (même doctrine que asset). Rien de sensible n'est
 * signalé sur cette famille (le coût calculé `computedCost` est un chiffre de
 * gestion courant, pas une donnée sensible au sens RH / santé).
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille composition (hors champs standard du socle). */
export const compositionCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "recipe", "bom"). */
  type: z.string().min(1),
  /** Article composé (référence `product`), cœur de la famille : requis. */
  productId: z.string().min(1),
  /** Composants : liste structurée par ligne (productId, quantité...), optionnel. */
  components: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Étapes de préparation ou d'assemblage : liste structurée, optionnel. */
  steps: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Rendement : quantité produite par la composition, optionnel. */
  yield: z.number().int().optional(),
  /** Coût calculé de la composition (montant), optionnel. */
  computedCost: z.number().optional(),
} as const;

/** Schéma complet de la famille composition (champs standard + champs métier). */
export const CompositionSchema = z.object({
  ...baseEntityFields,
  ...compositionCoreFields,
});

/** Type d'une composition. */
export type Composition = z.infer<typeof CompositionSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const CompositionCreateInputSchema = z.object({
  ...compositionCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une composition. */
export type CompositionCreateInput = z.infer<typeof CompositionCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const CompositionUpdateInputSchema = z
  .object({ ...compositionCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une composition. */
export type CompositionUpdateInput = z.infer<typeof CompositionUpdateInputSchema>;
